import { useEffect, useState } from 'react';
import useSWR from 'swr';
import Layout from '@layout/index';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { useAxios } from 'use-axios-client';

// ---------------------------------------------------------
import classNames from 'classnames';
import Listing from '@lib/models/listing';
import {
  formatMoney,
  formatNumber,
  getSlug,
  fetcher,
  truncateListingName,
  parseDateWithFormatMMMDDAndTime
} from '@lib/utils';

import {
  fetchAssociatedAgent,
  execAssociatedAgentQuery,
  getAssociatedAgentItem
} from '@lib/associatedAgent';

import { updateIsSibcyListingFilter } from '../../../store/slices/miscSlice';
import communityLookup from '@src/data/community-info.json';
import builderDevelopments from '@src/data/builder-development-info.json';
import { useAgentData } from '@lib/hooks';

// ---------------------------------------------------------

import Accordion from '@components/accordion';
import Button from '@components/button';
import Container from '@layout/container';
import Image from '@components/image';
import NotificationCard from '@components/notification-card';
import Modal from '@components/modal';
import MortgageCalculator from '@components/mortgage-calculator';
import NotesCardModal from '@components/notes/notes-card-modal';
import PostList from '@components/post-list';
import BrokerReciprocityCard from '@components/broker-reciprocity-card';
import PropertyImages from '@components/property-images';
import EnhancedContactForm from '@components/forms/enhanced';
import FormRealtorCards from '@components/forms/enhanced/realtor-cards';
import SellWithSibcy from '@components/sell-with-sibcy';
import ShareModal from '@components/modal/share-modal';
import SimilarProperties from '@components/similar-properties';
import TabSlider from '@components/tab-slider';
import Video from '@components/video';
import Map from '@components/map';
import Link from '@components/link';
import OpenHousesDropdown from '@components/openhouses-dropdown';
import RequestATourModal from '@components/request-a-tour-modal/component';
import TopicCarousel from '@components/topic-carousel';
import ContactForm from '@components/forms';
import OpenHouseModal from '@components/open-house-modal';
import { AddToCalendarButton } from 'add-to-calendar-button-react';

// ---------------------------------------------------------

import {
  page,
  page_container,
  address_wrapper,
  property_header_wrapper,
  property_header,
  address_container,
  stat_container,
  stat_container_mobile,
  stat,
  stat_wrapper,
  price_container,
  top,
  summary,
  details,
  section_header,
  left,
  top_section,
  overview_heading,
  right,
  right_margin_top_600,
  buttons,
  mobile_button,
  notification_modal,
  notes_modal,
  notification_card,
  visitor_stats,
  featured_section,
  featured_section_multiple_photos,
  featured_section_singular_photo,
  tabs,
  middle,
  calculator,
  bottom,
  browser_fix,
  listing_courtesy_of,
  refresh_date,
  notification_header,
  copyright_section,
  top_content_container,
  base_alert,
  base_alert_coming_soon,
  base_alert_pending,
  base_alert_price_change,
  base_alert_listed_on,
  directions_container,
  virtual_tour,
  directions_link,
  alerts_container,
  center,
  grid_container,
  featured_section_video,
  sticky_footer,
  is_mobile,
  is_desktop,
  concession_note_container,
  seller_concession_note,
  add_to_calendar,
  breadcrumb_container,
  breadcrumbs
} from './styles.module.scss';
import Icon from '@components/icon';

// ---------------------------------------------------------

const getCanonicalSlug = (fullCanonical) => {
  const canonicalPath = new URL(fullCanonical).pathname;
  const parts = canonicalPath.split('/');
  const slug = parts.slice(3).join('/');
  return slug;
};

export async function getServerSideProps(context) {
  // set caching headers to avoid caching in the browser
  context.res.setHeader('Cache-Control', 'no-store');

  const { agentid } = context.query;
  const { id: slugId, slug: slugArray } = context.params;
  const id = parseInt(slugId);

  const listing = new Listing(id);
  const details = await listing.details();
  let associatedAgent = null;

  // 404 if no pageData for the ID
  if (!details) {
    return {
      notFound: true
    };
  }

  // Redirect if the canonical URL doesn't match
  const { canonicalUrl } = details;
  const canonicalFromAPI = getCanonicalSlug(canonicalUrl);

  let slugAddress;
  if (slugArray?.length > 1) {
    slugAddress = slugArray.join('/');
  } else if (slugArray?.length === 1) {
    slugAddress = slugArray[0];
  } else {
    slugAddress = null;
  }

  let slugCheck = encodeURIComponent(slugAddress);

  // Apostrophe Edge Case
  slugCheck = slugCheck.replace("'", '%27');

  if (canonicalFromAPI !== slugCheck) {
    const { resolvedUrl } = context;

    let destinationUrl;
    if (slugAddress) {
      destinationUrl = resolvedUrl.replace(slugCheck, canonicalFromAPI);
    } else {
      destinationUrl = `/listing/${id}/${canonicalFromAPI}`;
      const queryString = resolvedUrl.split('?')[1];
      if (queryString) {
        destinationUrl += `?${queryString}`;
      }
    }

    return {
      redirect: {
        destination: destinationUrl,
        permanent: false
      }
    };
  }

  const fullCommunityLookup = Object.entries(communityLookup).map((x) => ({
    lookups: (x[1].fallbackSlugs || []).concat([x[0]]),
    slug: x[0]
  }));

  const listingCommunity =
    communityLookup[
      fullCommunityLookup.find((x) => x.lookups.some((y) => y === details?.communitySlug))?.slug ||
        details.communityFallbackSlug
    ];

  const builder =
    builderDevelopments.find((x) => x.category == 'Builder' && x.slug == details.builderSlug) || {};
  builder.url = `/builder/${builder?.slug}`;
  const development =
    builderDevelopments.find(
      (x) => x.category == 'Development' && x.slug == details.developmentSlug
    ) || {};
  development.url = `/development/${development?.slug}`;

  const nearbyCommunities = listingCommunity.nearbyCommunities.map((slug) => communityLookup[slug]);

  const similarProperties = await listing.getSimilarListings(details.similarListingIds);
  const interestRates = {
    15: (await listing.interest(15)).currentRate || '6',
    30: (await listing.interest(30)).currentRate || '5.5',
    lastUpdatedOn: (await listing.interest(30)).lastUpdatedOn
  };

  if (agentid) {
    associatedAgent = await fetchAssociatedAgent({ agentId: parseInt(agentid) });
  }

  return {
    props: {
      ...details,
      builder,
      development,
      interestRates,
      listingCommunity,
      nearbyCommunities,
      similarProperties,
      associatedAgent: getAssociatedAgentItem(associatedAgent)
    }
  };
}

// ---------------------------------------------------------

const ListingDetail = (props) => {
  const {
    alerts,
    interestRates,
    listingId: id,
    videoUrl,
    alertPills,
    virtualTourUrl,
    primaryDisplayAgent,
    secondaryDisplayAgent,
    isIdxListing,
    address,
    city,
    state,
    statusId,
    zip,
    bedroomCount,
    bathroomCount,
    builder,
    development,
    squareFootage,
    acres,
    price,
    priceFormatted,
    mlsNumber,
    mainPhoto,
    fullBathroomCount,
    partBathroomCount,
    photos,
    informationRequestDefaultComments,
    marketingRemarks,
    dataRefreshed,
    rooms,
    features,
    similarProperties,
    updateNoteListings,
    canonicalUrl,
    openHouses,
    listingCourtesyOf,
    mlsCopyright,
    listingUrl,
    longitude,
    latitude,
    propertyTypeDisplay,
    propertyTypeId,
    sharingServicesCollection,
    mapLocationExternalLink,
    directions,
    listingCommunity,
    nearbyCommunities,
    sellerConcessions,
    associatedAgent: associatedAgentProp,
    county,
    shouldShowSibcyAgent
  } = props;

  const truncatedListingName = truncateListingName(address, city, state, zip);

  // ---------------------------------------------------------

  const { saved, authenticated: isLoggedIn, userInfo } = useSelector((state) => state.user);
  const router = useRouter();
  const listing = new Listing(id);
  const dispatch = useDispatch();
  const {
    associatedAgent,
    setAssociatedAgent,
    setAssociatedAgentToSessionStorage,
    getAssociatedAgentFromSessionStorage
  } = useAgentData(associatedAgentProp);
  const [views, updateViews] = useState(0);
  const [selectedArea, setSelectedArea] = useState('Photos');
  const [isMedium, setIsMedium] = useState(null);
  const [currentListing, setCurrentListing] = useState(false);
  const [notificationModalIsOpen, setNotificationModalIsOpen] = useState(false);
  const [requestATourModalIsOpen, setRequestATourModalIsOpen] = useState(router.query.tourModal);
  const [preApprovalModalIsOpen, setPreApprovalModalIsOpen] = useState(false);
  const [notesModalIsOpen, setNotesModalIsOpen] = useState(false);
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [openHouseModalIsOpen, setOpenHouseModalIsOpen] = useState(false);
  const [savedListing, setSavedListing] = useState();
  const { data, refetch } = useAxios(`/api/user/account`);

  useEffect(() => {
    const currentUrl = router.asPath;
    const urlWithoutQuery = currentUrl.split('?')[0];
    const newQuery = { agentid: router.query.agentid };

    if (hasTour && !router.query.tourModal && requestATourModalIsOpen && router.query.agentid) {
      router.replace(
        { pathname: urlWithoutQuery, query: { ...newQuery, tourModal: true } },
        undefined,
        {
          shallow: true
        }
      );
    }
    if (hasTour && !router.query.tourModal && requestATourModalIsOpen && !router.query.agentid) {
      router.replace({ pathname: router.asPath, query: { tourModal: true } }, undefined, {
        shallow: true
      });
    }

    if (!requestATourModalIsOpen && router.query.tourModal && router.query.agentid) {
      const newPath = canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`;
      router.replace({ pathname: newPath, query: { ...newQuery } }, undefined, {
        shallow: true
      });
    }

    if (!requestATourModalIsOpen && router.query.tourModal && !router.query.agentid) {
      router.replace(canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`, undefined, {
        shallow: true
      });
    }

    if (router.query.openhouse && hasOpenHouse) {
      setOpenHouseModalIsOpen(true);
    }
  }, [requestATourModalIsOpen]);

  useEffect(() => {
    const obj = saved.find(({ listingId }) => props.listingId === listingId);
    setSavedListing(obj);
    setCurrentListing(obj);
  }, [saved]);

  useSWR(isLoggedIn ? `/api/user/listings/${id}` : null, fetcher, {
    onSuccess: ({ listingsByIds: savedListings }) => {
      const listing = savedListings.find((item) => item.listingId === id);
      setCurrentListing(listing);
    }
  });

  const fetchAgentData = async (variables, sibcylisting) => {
    const data = await execAssociatedAgentQuery(variables);

    if (typeof data?.agentSearch?.items[0] !== 'undefined' && data?.agentSearch?.items[0] != null) {
      if (!sibcylisting && !data?.associatedAgent) {
        setAssociatedAgent(data?.agentSearch?.items[0]);
      }
      setAssociatedAgentToSessionStorage(data?.agentSearch?.items[0]);
    }
  };

  const fetchAssociatedAgentData = async (variables) => {
    const data = await execAssociatedAgentQuery(variables);
    setAssociatedAgent(data?.agentSearch?.items[0] ?? []);
  };
  useEffect(() => {
    if (!isIdxListing) {
      dispatch(updateIsSibcyListingFilter({ group: 'isSibcyListing', value: true }));
      if (router.query.agentid) {
        let variables = {
          agentId: parseInt(router.query.agentid)
        };

        fetchAgentData(variables, true);
      }
    } else {
      dispatch(updateIsSibcyListingFilter({ group: 'isSibcyListing', value: false }));
      if (!associatedAgent && router.query.agentid) {
        let variables = {
          agentId: parseInt(router.query.agentid)
        };
        fetchAgentData(variables, false);
      }
    }

    if (!data?.associatedAgent) return;

    let variables = {
      agentId: data.associatedAgent
    };

    fetchAssociatedAgentData(variables);
  }, [router, router.pathname, isIdxListing, data?.associatedAgent, refetch]);

  const updateViewCount = async () => {
    updateViews(await listing.stats());
  };

  useEffect(() => {
    // set agent data to session storage if it was provided by the server
    if (associatedAgentProp) {
      setAssociatedAgentToSessionStorage(associatedAgentProp);
    } else {
      // set associated agent data if it exists in session storage
      // for a case where the user comes from a page with an agentid in the query
      const agentData = getAssociatedAgentFromSessionStorage();
      if (agentData) {
        setAssociatedAgent(agentData);
      }
    }
  }, [associatedAgentProp]);

  useEffect(() => {
    updateViewCount();

    const updateMedia = () => {
      setIsMedium(window.innerWidth < 1080);
    };
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateMedia);
    }
    return () => window.removeEventListener('resize', updateMedia);
  }, []);

  // ---------------------------------------------------------

  const selectedNotifications = [
    'shouldNotifyOnPriceChange',
    'shouldNotifyOnStatusChange',
    'shouldNotifyOnOpenHouseScheduled'
  ].filter((el) => currentListing?.[el]);

  const handleOpenNotificationModal = () => {
    if (isLoggedIn) {
      setNotificationModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/login';
      } else {
        router.push('/api/auth/login');
      }
    }
  };

  const onOpenHouseModalClose = () => {
    setOpenHouseModalIsOpen(false);
  };

  const onNotificationModalClose = () => {
    setNotificationModalIsOpen(false);
  };

  const notificationModalContent = (
    <div className={notification_modal}>
      <div className={notification_header}>
        <div className={browser_fix}>Set up N</div>
        <div className={browser_fix}>otifications!</div>
        <div>Don’t miss out on this property</div>
      </div>
      <div className={notification_card}>
        <NotificationCard
          savedListingId={savedListing?.savedListingId}
          button={{
            label: 'Update Notifications'
          }}
          selectedNotifications={selectedNotifications || []}
          checkboxes={{
            items: [
              {
                key: 'priceChange',
                label: 'Price change',
                value: 'shouldNotifyOnPriceChange'
              },
              {
                key: 'statusChange',
                label: 'Status change',
                value: 'shouldNotifyOnStatusChange'
              },
              {
                key: 'openHouse',
                label: 'An open house scheduled',
                value: 'shouldNotifyOnOpenHouseScheduled'
              }
            ],
            selectAllLabel: null
          }}
          propertyNotificationModalIsOpen={notificationModalIsOpen}
          setPropertyNotificationModalIsOpen={setNotificationModalIsOpen}
          heading="Notify me when this listing has:"
          image={photos[0]?.midSizeImageUrl}
          property={{
            address: address,
            city: city,
            listingId: id,
            priceFormatted: priceFormatted,
            state: state,
            zip: zip
          }}
          listingId={id}
          listingUrl={listingUrl}
          url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      </div>
    </div>
  );

  // ---------------------------------------------------------

  const preApprovalModalContent = (
    <div>
      <h2>Let's Move!</h2>
      <p>
        One of our loan officers will be in touch with you shortly to accelerate your pre-approval.
      </p>
      <ContactForm
        agentId={primaryDisplayAgent?.agentId || associatedAgent?.agentId || 0}
        theme="no-background-no-padding"
        linkLabel="Submit"
        endpoint="get pre-approved"
        endpointId={id}
        defaultFormValue={`I would like to be pre-approved for the property at ${address}. Please contact me with next steps.`}
        userInfo={userInfo}
      ></ContactForm>
    </div>
  );

  // ---------------------------------------------------------

  const getBreadcrumbs = () => {
    const getBreadcrumbItems = () => {
      const items = [];
      if (state)
        items.push({
          label: state,
          url: '/browse',
          query: { search_type: 'all_listings', state }
        });

      if (zip)
        items.push({
          label: zip,
          url: '/results',
          query: {
            zip: `${zip}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (county)
        items.push({
          label: `${county} County`,
          url: '/results',
          query: {
            counties: `${county}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (city)
        items.push({
          label: city,
          url: '/results',
          query: {
            places: `${city}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      items.push({ label: address, url: null });

      return items;
    };

    return (
      <div className={breadcrumbs}>
        {getBreadcrumbItems().map((item, index) => (
          <>
            <span key={index}>
              {item.url ? (
                <Link url={item.url} query={item.query} className={listing_courtesy_of}>
                  {item.label}
                </Link>
              ) : (
                <span className={listing_courtesy_of} key={index}>
                  {item.label}
                </span>
              )}
            </span>
            {index < getBreadcrumbItems().length - 1 && '•'}
          </>
        ))}
      </div>
    );
  };

  // ---------------------------------------------------------

  const handleOpenNotesModal = () => {
    if (isLoggedIn) {
      setNotesModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/login';
      } else {
        router.push('/api/auth/login');
      }
    }
  };

  const onNotesModalClose = () => {
    setNotesModalIsOpen(false);
  };

  const notesModalContent = (
    <div className={notes_modal}>
      <NotesCardModal
        image={photos[0]?.midSizeImageUrl}
        listingId={id}
        url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        listingUrl={`/listing/${id}`}
        property={{
          address,
          city,
          priceFormatted: formatMoney(price),
          state,
          zip
        }}
        updateNoteListings={updateNoteListings}
        modalIsOpen={notesModalIsOpen}
        setModalIsOpen={setNotesModalIsOpen}
        updateParent={false}
      />
    </div>
  );

  const shareButtons = (
    <div className={buttons}>
      <span
        className={mobile_button}
        onClick={handleOpenNotificationModal}
        onKeyDown={handleOpenNotificationModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notification"
          iconType="actions"
          label="Notify Me"
          theme="text-only"
          onClick={handleOpenNotificationModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={handleOpenNotesModal}
        onKeyDown={handleOpenNotesModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notes"
          iconType="actions"
          label="Add Note"
          theme="text-only"
          onClick={handleOpenNotesModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={() => setShareModalIsOpen(true)}
        onKeyDown={() => setShareModalIsOpen(true)}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="share"
          iconType="actions"
          label="Share"
          theme="text-only"
          onClick={() => setShareModalIsOpen(true)}
        />
      </span>
    </div>
  );

  // ---------------------------------------------------------

  const communities_slides = [listingCommunity].concat(nearbyCommunities || []).map((item) => ({
    excerpt: item?.excerpt,
    featuredImage: item?.featuredImage || {
      height: 960,
      url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
      width: 1440
    },
    linkLabel: item?.linkLabel || 'View Community',
    linkUrl: `/community/${item?.slug}`,
    title: item?.title
  }));

  // ---------------------------------------------------------

  const hasSecondaryAgent = !!secondaryDisplayAgent && !!primaryDisplayAgent;

  const pendingAlert = alerts?.filter((alert) => alert.type === 'Pending');
  const listedOnAlert = alerts?.filter((alert) => alert.type === 'ListedOn');
  const priceChangeAlert = alerts?.filter((alert) => alert.type === 'PriceChange');
  const comingSoonAlert = alerts?.filter((alert) => alert.type === 'ComingSoon');

  const hasTour = statusId !== 6 && statusId !== 14;
  const hasOpenHouse = openHouses[0];

  const dates = openHouses.map((openHouse) => {
    return parseDateWithFormatMMMDDAndTime(openHouse.formattedDateTimeDisplay);
  });

  // ---------------------------------------------------------

  const showMap = !!longitude && !!latitude && latitude !== 0 && latitude !== 0;
  const hasMapLocationExternalLink = mapLocationExternalLink && latitude !== 0 && longitude !== 0;

  // ---------------------------------------------------------

  const uniqVirtualTourUrl = virtualTourUrl !== videoUrl ? virtualTourUrl : null;

  // ---------------------------------------------------------
  return (
    <Layout
      canonicalUrl={canonicalUrl}
      meta={{
        description: marketingRemarks,
        image: {
          alt: address,
          url: mainPhoto?.midSizeImageUrl
        },
        title: truncatedListingName
      }}
      listingDetails={props}
      specializedPageSchema="realEstateSchema"
    >
      <div className={page}>
        <div className={page_container}>
          <div className={grid_container}>
            <div className={property_header_wrapper}>
              <div className={property_header}>
                <div className={address_wrapper}>
                  <div className={address_container}>
                    <h1>
                      {address}{' '}
                      <span>
                        {city}, {state} {zip}
                      </span>
                    </h1>
                  </div>
                </div>
                <div className={stat_container}>
                  {!!bedroomCount && (
                    <>
                      <div className={stat_wrapper}>
                        <div className={stat}>
                          <div>{bedroomCount}</div>
                          <div>Bed</div>
                        </div>
                      </div>
                    </>
                  )}
                  {!!bathroomCount && (
                    <>
                      <div className={stat_wrapper}>
                        <div className={stat}>
                          <div>
                            {partBathroomCount
                              ? `${fullBathroomCount}/${partBathroomCount}`
                              : fullBathroomCount}
                          </div>
                          <div>Bath</div>
                        </div>
                      </div>
                    </>
                  )}
                  {!!squareFootage && !!squareFootage !== '0' && (
                    <div className={classNames(stat_wrapper, is_desktop)}>
                      <div className={stat}>
                        <div>{formatNumber(squareFootage)}</div>
                        <div>Sq. Ft</div>
                      </div>
                    </div>
                  )}
                  {!!acres && !!acres !== '0' && (
                    <>
                      <div className={stat_wrapper}>
                        <div className={stat}>
                          <div>{acres}</div>
                          <div>Acres</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className={price_container}>
                  <div>{formatMoney(price)}</div>
                  <div>MLS# {mlsNumber}</div>
                </div>
              </div>

              <div className={classNames(is_mobile, stat_container_mobile)}>
                <div className={stat}>{bedroomCount && <div>{bedroomCount} BR</div>}</div>
                <div className={stat}>
                  {!!fullBathroomCount && !!partBathroomCount ? (
                    <div>
                      {fullBathroomCount}/{partBathroomCount} BA
                    </div>
                  ) : bathroomCount ? (
                    <div>{bathroomCount} BA</div>
                  ) : (
                    ''
                  )}
                </div>
                {!!squareFootage && !!squareFootage !== '0' && (
                  <div className={stat}>
                    <div>{formatNumber(squareFootage)} Sq. Ft</div>
                  </div>
                )}
                <div className={stat}>{!!acres && <div>{acres} AC</div>}</div>
              </div>
            </div>
            <div
              className={`${
                photos.length <= 1 && !isMedium && selectedArea === 'Photos'
                  ? classNames(featured_section, featured_section_singular_photo)
                  : selectedArea === 'Video Tour' || selectedArea === 'Map'
                  ? classNames(featured_section, featured_section_video)
                  : classNames(featured_section, featured_section_multiple_photos)
              } `}
            >
              {photos && selectedArea === 'Photos' && (
                <PropertyImages
                  canonicalUrl={canonicalUrl}
                  sharingServicesCollection={sharingServicesCollection}
                  virtualTourUrl={uniqVirtualTourUrl}
                  pageHasChat={isIdxListing}
                  property={{
                    address: address,
                    alertPills: alertPills,
                    images: photos.map((photo) => {
                      return {
                        height: photo.extraLargeImageHeight,
                        title: photo.title,
                        url: photo.extraLargeImageUrl,
                        width: photo.extraLargeImageWidth
                      };
                    }),
                    listingId: id,
                    listingUrl: canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`
                  }}
                />
              )}
              {selectedArea === 'Video Tour' && <Video title={address} url={videoUrl} />}
              {showMap && selectedArea === 'Map' && (
                <Map
                  longitude={longitude}
                  latitude={latitude}
                  address={address}
                  similarProperties={similarProperties}
                  propertyTypeId={propertyTypeId}
                  listingId={id}
                />
              )}
            </div>
            <div className={classNames(is_mobile, tabs)}>
              <TabSlider
                items={[
                  (videoUrl || showMap) && {
                    icon: {
                      name: 'photos',
                      type: 'filters'
                    },
                    label: 'Photos'
                  },
                  videoUrl && {
                    icon: {
                      name: 'video',
                      type: 'filters'
                    },
                    label: 'Video Tour'
                  },
                  showMap && {
                    icon: {
                      name: 'location',
                      type: 'filters'
                    },
                    label: 'Map'
                  }
                ].filter((item) => item)}
                setSelectedTabCallback={setSelectedArea}
              />
            </div>
          </div>
          <div className={top_content_container}>
            <div className={left}>
              <section className={top}>
                <div className={is_desktop}>
                  <div className={tabs}>
                    <TabSlider
                      items={[
                        (videoUrl || showMap) && {
                          icon: {
                            name: 'photos',
                            type: 'filters'
                          },
                          label: 'Photos'
                        },
                        videoUrl && {
                          icon: {
                            name: 'video',
                            type: 'filters'
                          },
                          label: 'Video Tour'
                        },
                        showMap && {
                          icon: {
                            name: 'location',
                            type: 'filters'
                          },
                          label: 'Map'
                        }
                      ].filter((item) => item)}
                      setSelectedTabCallback={setSelectedArea}
                    />
                  </div>
                </div>
                <div className={top_section}>
                  {uniqVirtualTourUrl && (
                    <div className={classNames(is_mobile, virtual_tour)}>
                      <Button
                        label="Virtual Tour"
                        theme="black"
                        url={virtualTourUrl}
                        target="_blank"
                        icon="video"
                      />
                    </div>
                  )}

                  <div className={alerts_container}>
                    {pendingAlert?.length > 0 && (
                      <div>
                        <h3 className={classNames(base_alert, base_alert_pending)}>
                          {pendingAlert[0]?.text}
                        </h3>
                      </div>
                    )}

                    {listedOnAlert?.length > 0 && (
                      <div>
                        <h3 className={classNames(base_alert, base_alert_listed_on)}>
                          {listedOnAlert[0]?.text}
                        </h3>
                      </div>
                    )}

                    {priceChangeAlert?.length > 0 && (
                      <div>
                        <h3 className={classNames(base_alert, base_alert_price_change)}>
                          {priceChangeAlert[0]?.text}
                        </h3>
                      </div>
                    )}

                    {comingSoonAlert?.length > 0 && (
                      <div>
                        <h3 className={classNames(base_alert, base_alert_coming_soon)}>
                          {comingSoonAlert[0]?.text}
                        </h3>
                      </div>
                    )}

                    {hasOpenHouse && (
                      <OpenHousesDropdown
                        label={openHouses[0].formattedDateTimeDisplay}
                        openHouses={openHouses}
                      />
                    )}
                  </div>

                  {hasOpenHouse && !isIdxListing && (
                    <div className={add_to_calendar}>
                      <AddToCalendarButton
                        label="Add Open House to Calendar"
                        name="Open House"
                        options={['Apple', 'Google', 'Outlook.com', 'Microsoft365']}
                        description={`See listing details and confirm open house time here:[br]${canonicalUrl}[br][br]For information about this property or to schedule a private showing:[br]${
                          primaryDisplayAgent?.displayPrimary
                        }[br]${primaryDisplayAgent?.displayPhone || ''}[br]${
                          primaryDisplayAgent?.displayEmail || ''
                        }`}
                        location={`${address}, ${city}, ${state} ${zip}`}
                        dates={dates}
                        timeZone="America/New_York"
                        hideBackground
                        styleLight="--btn-background: none; --btn-text: #c80002; --btn-hover-text: #c80002; --btn-border: none; --btn-shadow: none; --btn-hover-shadow: none; --font: ''__gothamMedium_876da8', '__gothamMedium_Fallback_876da8', system-ui, sans-serif; --btn-padding-x: 0; --btn-font-weight: 400;"
                        buttonStyle="3d"
                        listStyle="overlay"
                        trigger="click"
                        hideBranding
                        size="4"
                      ></AddToCalendarButton>
                    </div>
                  )}

                  <h2 className={overview_heading}>More About {address}</h2>

                  <div className={summary}>
                    {marketingRemarks}

                    <div className={concession_note_container}>
                      {statusId === 6 ? (
                        <>
                          <Button
                            label="Get Property Estimate"
                            url={`https://homevalue.sibcycline.com/${
                              primaryDisplayAgent?.agentId ||
                              router?.query?.agentid ||
                              associatedAgent?.agentId ||
                              ''
                            }`}
                          />
                          <div className={seller_concession_note}>
                            <Icon type="general" name="flame"></Icon>
                            <p>How does your home compare?</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Button label="Get Pre-Approved" onClick={setPreApprovalModalIsOpen} />
                          <div className={seller_concession_note}>
                            <Icon type="general" name="flame"></Icon>
                            <p>
                              {sellerConcessions
                                ? sellerConcessions
                                : 'Connect with a loan officer to get started!'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {(directions || hasMapLocationExternalLink) && statusId != 6 && (
                      <div className={directions_container}>
                        {hasMapLocationExternalLink ? (
                          <Link
                            url={mapLocationExternalLink}
                            target="_blank"
                            className={directions_link}
                          >
                            <Icon type="filters" name="location"></Icon>
                            <h3>Directions to this Listing</h3>
                          </Link>
                        ) : (
                          <h3>Directions to this Listing</h3>
                        )}
                        <p>{directions && `: ${directions}`}</p>
                      </div>
                    )}
                    {dataRefreshed && (
                      <div className={refresh_date}>
                        <p>Information Refreshed: {dataRefreshed}</p>
                      </div>
                    )}
                    {shareButtons}
                  </div>

                  <div className={center}>
                    {shouldShowSibcyAgent ? (
                      <EnhancedContactForm
                        primaryAgent={primaryDisplayAgent}
                        secondaryAgent={secondaryDisplayAgent}
                        defaultFormValue={informationRequestDefaultComments}
                        hasTour={hasTour}
                        hasSecondaryAgent={hasSecondaryAgent}
                        listingId={id}
                        agentId="0"
                        sticky={true}
                        setModalIsOpen={setRequestATourModalIsOpen}
                        endpoint="property information request"
                      >
                        <FormRealtorCards
                          hasTour={hasTour}
                          hasSecondaryAgent={hasSecondaryAgent}
                          setModalIsOpen={setRequestATourModalIsOpen}
                          primaryContact={primaryDisplayAgent}
                          secondaryContact={secondaryDisplayAgent}
                        />
                      </EnhancedContactForm>
                    ) : !associatedAgent ? (
                      <BrokerReciprocityCard
                        hasTour={hasTour}
                        listingId={id}
                        defaultFormValue={informationRequestDefaultComments}
                        sticky={false}
                        setModalIsOpen={setRequestATourModalIsOpen}
                      />
                    ) : (
                      <EnhancedContactForm
                        hasTour={hasTour}
                        setModalIsOpen={setRequestATourModalIsOpen}
                        primaryAgent={primaryDisplayAgent || associatedAgent}
                        sticky={false}
                        listingId={id}
                        agentId={associatedAgent.agentId}
                        defaultFormValue={informationRequestDefaultComments}
                        endpoint="property information request"
                      >
                        <FormRealtorCards
                          hasTour={hasTour}
                          primaryContact={associatedAgent}
                          setModalIsOpen={setRequestATourModalIsOpen}
                        />
                      </EnhancedContactForm>
                    )}
                  </div>

                  <h2 className={section_header}>Property Details</h2>
                  {features && (
                    <div className={details}>
                      <ul>
                        {mlsNumber && (
                          <li>
                            <h3>MLS#:</h3> {mlsNumber}
                          </li>
                        )}
                        {propertyTypeDisplay && (
                          <li>
                            <h3>Type:</h3> {propertyTypeDisplay}
                          </li>
                        )}
                        {!!squareFootage && !!squareFootage !== '0' && (
                          <li>
                            <h3>Sq. Ft:</h3>
                            {formatNumber(squareFootage)}
                          </li>
                        )}
                        {builder?.slug && (
                          <li key={builder.slug}>
                            <h3>Builder:</h3>
                            <Button
                              label={builder.builderDevelopmentName}
                              theme="text-only-red"
                              url={builder.url}
                            />
                          </li>
                        )}
                        {county && (
                          <li>
                            <h3>County:</h3> {county}
                          </li>
                        )}
                        {development?.slug && (
                          <li key={development.slug}>
                            <h3>Development:</h3>
                            <Button
                              label={development.builderDevelopmentName}
                              theme="text-only-red"
                              url={development.url}
                            />
                          </li>
                        )}
                        {features.map((feature) => {
                          return (
                            <li key={feature.name}>
                              <h3>{feature.name}:</h3>
                              {feature.value}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {rooms.length > 0 && (
                    <>
                      <h2 className={section_header}>Rooms</h2>
                      <div className={details}>
                        <ul>
                          {rooms.map((room) => {
                            return (
                              <li key={room.name}>
                                <h3>{room.name}:</h3>
                                {room.description}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </>
                  )}

                  {process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' && (
                    <div className={visitor_stats}>
                      <div>
                        <h3>Online Views:</h3>
                        {views}
                      </div>
                    </div>
                  )}

                  {listingCourtesyOf && (
                    <div className={listing_courtesy_of}>
                      <span>This listing courtesy of </span>
                      <span>{listingCourtesyOf}</span>
                    </div>
                  )}
                </div>
              </section>
              <section className={middle}>
                {communities_slides?.length > 0 && (
                  <>
                    <h2 className={section_header}>
                      {communities_slides?.length > 1
                        ? `Explore ${listingCommunity.title} & Surrounding Area`
                        : 'Lifestyle in the Surrounding Area'}
                    </h2>
                    <TopicCarousel
                      carouselId="custom-nearby-communities"
                      theme="ldp"
                      slidesCollection={{ cards: communities_slides }}
                    />
                  </>
                )}
                <Accordion
                  listItems={{
                    items: [
                      {
                        content: {
                          value: (
                            <div className={calculator}>
                              <MortgageCalculator
                                price={price}
                                currentRates={interestRates}
                                lastUpdatedOn={interestRates?.lastUpdatedOn}
                              />
                            </div>
                          )
                        },
                        id: 'monthly-cost',
                        label: 'Monthly Cost'
                      }
                    ]
                  }}
                  // Signals which Accordion Item to have open on mount
                  openAccordionId={['monthly-cost']}
                />
                <div className={breadcrumb_container}>{getBreadcrumbs()}</div>
              </section>
            </div>
            <div
              className={classNames(right, {
                [right_margin_top_600]: photos.length <= 1 && selectedArea === 'Photos'
              })}
            >
              {shouldShowSibcyAgent ? (
                <EnhancedContactForm
                  defaultFormValue={informationRequestDefaultComments}
                  primaryAgent={primaryDisplayAgent}
                  secondaryAgent={secondaryDisplayAgent}
                  hasTour={hasTour}
                  hasSecondaryAgent={hasSecondaryAgent}
                  listingId={id}
                  agentId="0"
                  sticky={true}
                  setModalIsOpen={setRequestATourModalIsOpen}
                  endpoint="property information request"
                >
                  <FormRealtorCards
                    hasTour={hasTour}
                    hasSecondaryAgent={hasSecondaryAgent}
                    setModalIsOpen={setRequestATourModalIsOpen}
                    primaryContact={primaryDisplayAgent}
                    secondaryContact={secondaryDisplayAgent}
                  />
                </EnhancedContactForm>
              ) : !associatedAgent ? (
                <BrokerReciprocityCard
                  hasTour={hasTour}
                  listingId={id}
                  defaultFormValue={informationRequestDefaultComments}
                  sticky={true}
                  setModalIsOpen={setRequestATourModalIsOpen}
                />
              ) : (
                <EnhancedContactForm
                  hasTour={hasTour}
                  setModalIsOpen={setRequestATourModalIsOpen}
                  primaryAgent={primaryDisplayAgent || associatedAgent}
                  secondaryAgent={secondaryDisplayAgent}
                  sticky={true}
                  listingId={id}
                  agentId={associatedAgent.agentId}
                  defaultFormValue={informationRequestDefaultComments}
                  endpoint="property information request"
                >
                  <FormRealtorCards
                    hasTour={hasTour}
                    setModalIsOpen={setRequestATourModalIsOpen}
                    primaryContact={associatedAgent}
                  />
                </EnhancedContactForm>
              )}
            </div>
          </div>
        </div>

        <section className={bottom}>
          {similarProperties.length ? (
            <SimilarProperties properties={similarProperties} address={address} />
          ) : null}
          <Container width="full" layout="Two thirds, one third" gutter="25">
            <SellWithSibcy />
            <PostList title="Must reads" />
          </Container>
        </section>

        <Container>
          <section className={copyright_section}>
            <div>
              <Image
                layout="fill"
                image={{
                  height: '22px',
                  src: mlsCopyright.logoUrl,
                  width: '96px'
                }}
              />
            </div>

            <div>{mlsCopyright.description}</div>
          </section>
        </Container>
      </div>

      {/**
       * --------------------------------------------- Modals
       * Separate section for modals, to avoid duplication for Mobile / Desktop
       */}

      {shareModalIsOpen && (
        <ShareModal
          modalIsOpen={shareModalIsOpen}
          onModalClose={() => setShareModalIsOpen(false)}
          sharingServicesCollection={sharingServicesCollection}
          copyUrl={canonicalUrl}
        />
      )}

      {preApprovalModalIsOpen && (
        <Modal
          modalIsOpen={preApprovalModalIsOpen}
          onModalClose={() => setPreApprovalModalIsOpen(false)}
          content={preApprovalModalContent}
          theme="pre-approval"
        />
      )}

      {notesModalIsOpen && (
        <Modal
          modalIsOpen={notesModalIsOpen}
          onModalClose={onNotesModalClose}
          content={notesModalContent}
          theme="notes"
        />
      )}

      {notificationModalIsOpen && (
        <Modal
          modalIsOpen={notificationModalIsOpen}
          onModalClose={onNotificationModalClose}
          content={notificationModalContent}
        />
      )}

      {requestATourModalIsOpen && (
        <RequestATourModal
          listingId={id}
          agentId={primaryDisplayAgent?.agentId || associatedAgent?.agentId || 0}
          modalIsOpen={requestATourModalIsOpen}
          setModalIsOpen={setRequestATourModalIsOpen}
          defaultFormValue={informationRequestDefaultComments}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      )}

      {openHouseModalIsOpen && hasOpenHouse && (
        <OpenHouseModal
          modalIsOpen={openHouseModalIsOpen}
          setModalIsOpen={onOpenHouseModalClose}
          agentId={
            router?.query?.openhouse ||
            primaryDisplayAgent?.agentId ||
            router?.query?.agentid ||
            associatedAgent?.agentId ||
            0
          }
          listingId={id}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
          openHouses={openHouses}
          directionsUrl={hasMapLocationExternalLink ? mapLocationExternalLink : null}
        />
      )}

      {hasTour && (
        <div className={sticky_footer}>
          <div>
            <Button
              theme="red"
              onClick={() => setRequestATourModalIsOpen(true)}
              label="Request Tour"
              iconType="general"
              icon="clock"
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ListingDetail;
